import { IMotorLovs } from '..';
import { IAbstractRecord } from '@models';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';

export const initialValues = {
  nameOnLicenseEn: '',
  nameOnLicenseAr: '',
  vignetteCode: '',
  vignette: '',
  plateCode: '',
  plateNumber: '',
  usageType: '',
  bodyType: '',
  engineType: '',
  make: '',
  model: '',
  color: '',
  yearOfMake: '',
  engine: '',
  weight: '',
  engineSize: '',
  seatingCapacity: '',
  chassis: '',
  horsePower: '',
  carValue: '',
  agencyRepairYear: '',
  depreciationYears: '',
};

export const drawerSections = (
  lovs: IMotorLovs,
  values: IAbstractRecord,
  externalCode: string
): IDynamicFormWidgetSection[] => {
  return [
    {
      inputs: {
        nameOnLicenseEn: {
          name: 'nameOnLicenseEn',
          title: 'Name on License (EN)',
          type: FormFieldType.text,
          placeholder: 'Name on License (EN)',
          value: '',
          required: true,
          disabled: false,
        },
        nameOnLicenseAr: {
          name: 'nameOnLicenseAr',
          title: 'Name on License (AR)',
          type: FormFieldType.text,
          placeholder: 'Name on License (AR)',
          value: '',
          required: true,
          disabled: false,
        },
        vignetteCode: {
          name: 'vignetteCode',
          title: 'Vignette Code',
          type: FormFieldType.text,
          placeholder: 'Vignette Code',
          value: '',
          required: externalCode === '50',
          disabled: externalCode !== '50',
          hidden: externalCode !== '50',
        },
        vignette: {
          name: 'vignette',
          title: 'Vignette',
          type: FormFieldType.number,
          placeholder: 'Vignette',
          value: '',
          required: externalCode === '50',
          disabled: externalCode !== '50',
          hidden: externalCode !== '50',
        },
        plateCode: {
          name: 'plateCode',
          title: 'Plate Code',
          type: FormFieldType.select,
          placeholder: 'Plate Code',
          value: '',
          required: false,
          disabled: true,
          selectOptions: lovs.plateCodes,
        },
        plateNumber: {
          name: 'plateNumber',
          title: 'Plate Number',
          type: FormFieldType.number,
          placeholder: 'Plate Number',
          value: '',
          required: false,
          disabled: false,
        },
        usageType: {
          name: 'usageType',
          title: 'Usage',
          type: FormFieldType.select,
          placeholder: 'Usage',
          value: '',
          required: true,
          disabled: true,
          selectOptions: lovs.usageTypes,
        },
        bodyType: {
          name: 'bodyType',
          title: 'Body',
          type: FormFieldType.select,
          placeholder: 'Body',
          value: '',
          required: true,
          disabled: false,
          selectOptions: lovs.bodyTypes,
        },
        engineType: {
          name: 'engineType',
          title: 'Engine Type',
          type: FormFieldType.text,
          placeholder: 'Engine Type',
          value: '',
          required: true,
          disabled: true,
        },
        make: {
          name: 'make',
          title: 'Make',
          type: FormFieldType.select,
          placeholder: 'Make',
          value: '',
          required: true,
          disabled: true,
          selectOptions: lovs.brands,
        },
        model: {
          name: 'model',
          title: 'Model',
          type: FormFieldType.select,
          placeholder: 'Model',
          value: '',
          required: true,
          disabled: true,
          selectOptions: lovs.model,
        },
        color: {
          name: 'color',
          title: 'Color',
          type: FormFieldType.select,
          placeholder: 'Color',
          value: '',
          required: false,
          disabled: false,
          selectOptions: lovs.colors,
        },
        yearOfMake: {
          name: 'yearOfMake',
          title: 'Year of Make',
          type: FormFieldType.number,
          placeholder: 'Year of Make',
          value: '',
          required: externalCode !== '51',
          disabled: externalCode === '50',
        },
        engine: {
          name: 'engine',
          title: 'Engine',
          type: FormFieldType.text,
          placeholder: 'Engine',
          value: '',
          required: true,
          disabled: false,
        },
        weight: {
          name: 'weight',
          title: 'Weight (in Tons)',
          type: FormFieldType.number,
          placeholder: 'Weight (in Tons)',
          value: '',
          conditionalRequired: (values) => {
            return ['VAN', 'PICKUP', 'F_TANKER'].includes(values.bodyType.Code);
          },
          conditionalHidden: (values) => {
            return !['VAN', 'PICKUP', 'F_TANKER'].includes(
              values.bodyType.Code
            );
          },
          disabled: true,
        },
        engineSize: {
          name: 'engineSize',
          title: 'Engine Size (in cc)',
          type: FormFieldType.number,
          placeholder: 'Engine Size (in cc)',
          value: '',
          conditionalRequired: (values) =>
            values.bodyType.Code === 'MOTORCYCLE',
          conditionalHidden: (values) => values.bodyType.Code !== 'MOTORCYCLE',
          disabled: true,
        },
        seatingCapacity: {
          name: 'seatingCapacity',
          title: 'Seating Capacity',
          type: FormFieldType.number,
          placeholder: 'Seating Capacity',
          value: '',
          disabled: ['MICROBUS', 'MINI_BUS', 'BUS', 'MINIVAN'].includes(
            values?.bodyType?.Code
          ),
        },
        chassis: {
          name: 'chassis',
          title: 'Chassis',
          type: FormFieldType.text,
          placeholder: 'Chassis',
          value: '',
          required: true,
        },
        horsePower: {
          name: 'horsePower',
          title: 'Horse Power',
          type: FormFieldType.number,
          placeholder: 'Horse Power',
          value: '',
          disabled: true,
        },
        carValue: {
          name: 'carValue',
          title: 'Car Value',
          type: FormFieldType.currency,
          placeholder: 'Car Value',
          value: '',
          required: externalCode === '51',
          disabled: true,
          hidden: externalCode !== '51',
        },
        agencyRepairYear: {
          name: 'agencyRepairYear',
          title: 'Agency Repair Years',
          type: FormFieldType.number,
          placeholder: 'Agency Repair Years',
          value: '',
          required: externalCode === '51',
          disabled: true,
          hidden: externalCode !== '51',
        },
        depreciationYears: {
          name: 'depreciationYears',
          title: 'Depreciation Years',
          type: FormFieldType.number,
          placeholder: 'Depreciation Years',
          value: '',
          required: externalCode === '51',
          hidden: externalCode !== '51',
        },
      },
    },
  ];
};
